import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, ProSidebar, MenuItem } from 'react-pro-sidebar';
import "react-pro-sidebar/dist/css/styles.css";
import { Link } from 'react-router-dom';
import { useTheme, Box, IconButton, Typography } from '@mui/material';
import CollapseSidebarIcon from '@mui/icons-material/MenuOpenOutlined';
import ExpandSidebarIcon from '@mui/icons-material/MenuOutlined';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import CalendarIcon from '@mui/icons-material/CalendarMonthOutlined';
import PatientsIcon from '@mui/icons-material/PeopleOutlined';
import DoctorsIcon from '@mui/icons-material/Diversity1Outlined';
import PharmaciesIcon from '@mui/icons-material/LocalPharmacyOutlined';
import { tokens } from '../theme';
import { calendarBaseRoute, dashboardBaseRoute, doctorsBaseRoute, patientsBaseRoute, pharmaciesBaseRoute } from '../types';

interface SidebarItemProps {
  icon: ReactNode;
  selected: string;
  setSelected: React.Dispatch<React.SetStateAction<string>>; 
  title: string;
  to: string;
} 

const Item = ({ icon, selected, setSelected, title, to }: SidebarItemProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <MenuItem
      active={selected === title}
      icon={icon}
      onClick={() => setSelected(title)}
      style={{ color: colors.grey[100] }}
    >
      <Typography variant="h6">{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const MainSidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selected, setSelected] = useState("Dashboard");

  useEffect(() => {
    if (location.pathname.startsWith(dashboardBaseRoute)) {
      setSelected("Dashboard");
    } else if (location.pathname.startsWith(calendarBaseRoute)) {
      setSelected("Calendar");
    } else if (location.pathname.startsWith(patientsBaseRoute)) {
      setSelected("Patients");
    } else if (location.pathname.startsWith(doctorsBaseRoute)) {
      setSelected("Doctors");
    } else if (location.pathname.startsWith(pharmaciesBaseRoute)) {
      setSelected("Pharmacies");
    }
  }, [location.pathname]);

  return (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: `${colors.primary[400]} !important`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 35px 5px 20px !important",
        },
        "& .pro-inner-item:hover": {
          color: "#868dfb !important",
        },
        "& .pro-menu-item.active": {
          color: "#6870fa !important",
        },
      }}
    >
      <ProSidebar 
        collapsed={isCollapsed} 
        style={{ 
          height: '100%', 
          overflowY: 'auto' 
        }}
      >
        <Menu iconShape="square">
          <MenuItem 
            icon={isCollapsed ? <ExpandSidebarIcon /> : undefined}
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={{
              margin: "6px 0 16px 0",
              color: colors.grey[100]
            }}
          >
            {!isCollapsed && (
              <Box 
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Typography variant="h5" color={colors.grey[100]}>
                  FILLWARE CLOUD
                </Typography>
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <CollapseSidebarIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          <Box paddingLeft={isCollapsed ? undefined : "2%"}>
            <Item
              icon={<DashboardIcon />}
              selected={selected}
              setSelected={setSelected}
              title="Dashboard"
              to="/dashboard"
            />
            <Item
              icon={<CalendarIcon />}
              selected={selected}
              setSelected={setSelected}
              title="Calendar"
              to="/calendar"
            />
            <Item
              icon={<PatientsIcon />}
              selected={selected}
              setSelected={setSelected}
              title="Patients"
              to="/patients"
            />
            <Item
              icon={<DoctorsIcon />}
              selected={selected}
              setSelected={setSelected}
              title="Doctors"
              to="/doctors"
            />
            <Item
              icon={<PharmaciesIcon />}
              selected={selected}
              setSelected={setSelected}
              title="Pharmacies"
              to="/pharmacies"
            />
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default MainSidebar;